import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDepartments } from '../../../hooks/useDepartments';
import { stateAllocationService } from '../../../services/stateAllocationService';
import { stateDepartmentsService } from '../../../services/stateDepartmentsService';
import LoadingSpinner from '../../common/LoadingSpinner';
import { EmptyState } from '../../common/EmptyState';
import UniversalFilters from '../../common/UniversalFilters';
import ExportPDFButton from '../../common/ExportPDFButton';
import ExportExcelButton from '../../common/ExportExcelButton';
import { getFilterConfig } from '../../../constants/filterConfigs';
import { useFilters } from '../../../hooks/useFilters';
import { formatAllocationAmount } from '../../../constants/stateAllocationConstants';
import { financialYearUtils } from '../../../utils/helpers';
import { exportStateShareAllocationDepartmentWisePDF } from '../../../utils/pdfExport';
import { exportDepartmentalAllocationToExcel } from '../../../utils/excelExport';

const DepartmentalAllocation = () => {
  const { user } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const { departments, loading: loadingDepartments, getDepartmentLabel } = useDepartments();
  
  const [loading, setLoading] = useState(true);
  const [departmentSummary, setDepartmentSummary] = useState([]);
  const [filteredSummary, setFilteredSummary] = useState([]);
  const [error, setError] = useState('');

  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const {
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters
  } = useFilters({
    search: '',
    financialYear: ''
  });

  const filterConfig = getFilterConfig('DEPARTMENTAL_ALLOCATION');

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const cardPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  useEffect(() => {
    // Only load data if financial year is selected
    if (departments.length > 0 && filters.financialYear) {
      loadDepartmentalSummary();
    } else {
      // Clear data when no financial year is selected
      setDepartmentSummary([]);
      setFilteredSummary([]);
      setLoading(false);
    }
  }, [filters.financialYear, departments]);

  useEffect(() => {
    applySearchFilter();
  }, [departmentSummary, filters.search, departments]);

  const loadDepartmentalSummary = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load project allocations and state departments data in parallel
      const [allocationsResult, stateDeptResult] = await Promise.all([
        stateAllocationService.getDepartmentalAllocationSummary(filters.financialYear),
        stateDepartmentsService.getDepartmentsData(filters.financialYear)
      ]);
      
      if (allocationsResult.success) {
        // Create a map of approved allocations from state departments data
        const approvedAllocationsMap = {};
        if (stateDeptResult.success && stateDeptResult.data?.departments) {
          stateDeptResult.data.departments.forEach(dept => {
            const deptCode = dept.value || dept.id;
            approvedAllocationsMap[deptCode] = parseFloat(dept.amount) || 0;
          });
        }

        // Enhance summary with calculated values
        const enhancedSummary = allocationsResult.data.map(dept => {
          const approvedAllocation = approvedAllocationsMap[dept.department] || 0;
          
          // Calculate totalAmount from the three source columns
          const totalAmount = (dept.clearedAgainstAllocation || 0) + 
                             (dept.clearedFromCommonPool || 0) + 
                             (dept.additionalStateShare || 0);
          
          // Calculate balance at department level: Approved - Total Cleared
          const balanceAgainstAllocation = Math.max(0, 
            approvedAllocation - (dept.clearedAgainstAllocation || 0)
          );
          
          return {
            ...dept,
            totalAmount,
            approvedAllocation,
            balanceAgainstAllocation
          };
        });
        
        // Sort by department priority
        const sortedSummary = [...enhancedSummary].sort((a, b) => {
          const deptA = departments.find(d => d.departmentCode === a.department);
          const deptB = departments.find(d => d.departmentCode === b.department);
          
          const priorityA = deptA?.priority || 999;
          const priorityB = deptB?.priority || 999;
          
          return priorityA - priorityB;
        });
        
        setDepartmentSummary(sortedSummary);
      }
    } catch (error) {
      console.error('Error loading departmental summary:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const applySearchFilter = () => {
    if (!departments.length) return;
    
    let filtered = [...departmentSummary];

    // Apply search filter
    if (filters.search && filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      filtered = filtered.filter(dept => {
        const deptLabel = getDepartmentLabel(dept.department).toLowerCase();
        return deptLabel.includes(searchLower);
      });
    }

    setFilteredSummary(filtered);
  };

  const handleExportPDF = () => {
    try {
      setExporting(true);
      
      const exportData = {
        departments: filteredSummary.map(dept => ({
          ...dept,
          departmentName: getDepartmentLabel(dept.department)
        }))
      };
      
      exportStateShareAllocationDepartmentWisePDF(exportData, filters);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = () => {
    try {
      setExportingExcel(true);
      
      const exportData = {
        departments: filteredSummary
      };
      
      exportDepartmentalAllocationToExcel(exportData, filters, getDepartmentLabel);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      setError('Failed to export Excel. Please try again.');
    } finally {
      setExportingExcel(false);
    }
  };

  // Calculate grand totals for filtered data
  const grandTotals = filteredSummary.reduce((totals, dept) => ({
    approvedAllocation: totals.approvedAllocation + dept.approvedAllocation,
    clearedAgainstAllocation: totals.clearedAgainstAllocation + dept.clearedAgainstAllocation,
    clearedFromCommonPool: totals.clearedFromCommonPool + dept.clearedFromCommonPool,
    additionalStateShare: totals.additionalStateShare + dept.additionalStateShare,
    totalAmount: totals.totalAmount + dept.totalAmount,
    balanceAgainstAllocation: totals.balanceAgainstAllocation + dept.balanceAgainstAllocation
  }), {
    approvedAllocation: 0,
    clearedAgainstAllocation: 0,
    clearedFromCommonPool: 0,
    additionalStateShare: 0,
    totalAmount: 0,
    balanceAgainstAllocation: 0
  });

  if (loading || loadingDepartments) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading departmental allocation..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <style>{`
        .table-container {
          max-height: calc(100vh - 200px);
          overflow-y: auto;
          position: relative;
        }
        
        .sticky-header thead {
          position: sticky;
          top: 0;
          z-index: 20;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .sticky-header thead th {
          background: linear-gradient(to right, #f9fafb, #f3f4f6);
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${titleSize} font-bold text-text-primary mb-2`}>Department Wise</h1>
          <p className="text-text-secondary">
            Department-wise allocation summary and balance tracking
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <ExportExcelButton 
            onClick={handleExportExcel}
            loading={exportingExcel}
            disabled={!filteredSummary || filteredSummary.length === 0}
          />
          
          <ExportPDFButton 
            onClick={handleExportPDF}
            loading={exporting}
            disabled={!filteredSummary || filteredSummary.length === 0}
          />
          
          <UniversalFilters
            config={filterConfig}
            values={filters}
            onChange={handleFilterChange}
            onClearFilters={clearFilters}
            mode="dropdown"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-error-subtle border border-border-error rounded-xl p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-error font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Departmental Allocation Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {filteredSummary.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 002 2z" />
                </svg>
              }
              title={filters.search ? "No departments match your search" : "No allocation data found"}
              description={
                filters.search 
                  ? `No departments found matching "${filters.search}". Try different search terms.`
                  : "No project allocations available for the selected financial year"
              }
              actionLabel="Clear Filters"
              onAction={clearFilters}
            />
          </div>
        ) : (
          <div className="table-container">
            <table className="min-w-full text-xs sticky-header">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16 bg-gray-50">
                    S. No.
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-40 bg-gray-50">
                    Department
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-blue-700 uppercase tracking-wider border-r border-blue-200 w-24 bg-blue-50">
                    Cleared<br />against<br />allocation
                    <br />
                    <span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-blue-700 uppercase tracking-wider border-r border-blue-200 w-24 bg-blue-50">
                    Cleared from<br />Common Pool
                    <br />
                    <span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-blue-700 uppercase tracking-wider border-r border-blue-200 w-24 bg-blue-50">
                    Additional<br />State Share
                    <br />
                    <span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-green-700 uppercase tracking-wider border-r border-green-200 w-20 bg-green-50">
                    Total amount
                    <br />
                    <span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-green-700 uppercase tracking-wider border-r border-green-200 w-24 bg-green-50">
                    Approved<br />allocation
                    <br />
                    <span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-green-700 uppercase tracking-wider w-24 bg-green-50">
                    Balance against<br />allocation
                    <br />
                    <span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredSummary.map((dept, index) => (
                  <tr key={dept.department} className="hover:bg-blue-50 transition-colors duration-200">
                    <td className="px-3 py-3 text-xs text-gray-600 font-semibold border-r border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">
                        {index + 1}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 font-semibold border-r border-gray-200 bg-gray-50">
                      {getDepartmentLabel(dept.department)}
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-blue-200 bg-blue-50">
                      {formatAllocationAmount(dept.clearedAgainstAllocation)}
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-blue-200 bg-blue-50">
                      {formatAllocationAmount(dept.clearedFromCommonPool)}
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-blue-200 bg-blue-50">
                      {formatAllocationAmount(dept.additionalStateShare)}
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-green-200 bg-green-50">
                      {formatAllocationAmount(dept.totalAmount)}
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-green-200 bg-green-50">
                      {formatAllocationAmount(dept.approvedAllocation)}
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium bg-green-50">
                      {formatAllocationAmount(dept.balanceAgainstAllocation)}
                    </td>
                  </tr>
                ))}

                {/* Grand Total Row */}
                <tr className="bg-gray-700 text-white font-bold border-t-4 border-gray-600">
                  <td className="px-3 py-3 border-r border-gray-600"></td>
                  <td className="px-3 py-3 text-xs font-bold border-r border-gray-600">
                    Grand Total{hasActiveFilters && ' (Filtered)'}
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-600">
                    {formatAllocationAmount(grandTotals.clearedAgainstAllocation)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-600">
                    {formatAllocationAmount(grandTotals.clearedFromCommonPool)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-600">
                    {formatAllocationAmount(grandTotals.additionalStateShare)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-600">
                    {formatAllocationAmount(grandTotals.totalAmount)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-600">
                    {formatAllocationAmount(grandTotals.approvedAllocation)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-bold">
                    {formatAllocationAmount(grandTotals.balanceAgainstAllocation)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentalAllocation;